import type { UseCaseData } from "./types";

export const collegeStudentsUseCase: UseCaseData = {
  slug: "college-students",
  seo: {
    title:
      "PassiveAgents for College Students: Ship Side Projects Between Classes",
    description:
      "Use AI coding agents to keep your side project moving while you're in class, at work, or studying. Assign tasks, let agents build, review what shipped.",
    canonical:
      "https://www.passiveagents.com/use-cases/ai-agents-for-college-students",
  },
  breadcrumbs: [
    { label: "Home", href: "/home" },
    { label: "Use Cases", href: "/use-cases" },
    { label: "College Students" },
  ],
  h1: "Keep your side project moving between classes",
  shortAnswer:
    "College students have the ideas and the skills but not the hours. They spend weekends in coding sprints that never seem to close the gap. PassiveAgents lets them assign feature tasks and bug fixes to coding agents that work during the week — so by the weekend, there's something to review instead of something to start.",

  whoThisIsFor: {
    headline: "Who this is for",
    audiences: [
      {
        type: "CS students building portfolio projects",
        description:
          "You need working code to show recruiters, not a half-finished repo. Agents keep your portfolio project moving forward between study sessions so you have something real to demo.",
      },
      {
        type: "Students with part-time jobs",
        description:
          "Your 10 free hours a week disappear fast between classes, homework, and shifts. Agents make those hours go further by handling implementation while you handle everything else.",
      },
      {
        type: "Student founders",
        description:
          "You have a startup idea but a full course load. Agents handle execution while you handle school — so you're not choosing between your GPA and your product.",
      },
    ],
  },

  commonProblems: [
    {
      problem: "Time fragmented across everything",
      detail:
        "Classes, homework, jobs, social life — you never get a 4-hour block to actually build. By the time you sit down and load the project back into your head, you have 45 minutes left.",
    },
    {
      problem: "Side projects stall after initial momentum",
      detail:
        "A week without touching your project means relearning your own code. The longer the gap, the harder it is to get back in. Projects that were exciting in September sit unfinished in December.",
    },
    {
      problem: "Recruiting pressure to show shipped work",
      detail:
        "Interviewers want to see working projects, not ideas. A GitHub repo full of half-finished branches doesn't tell the story you want. You need working code, not just code knowledge.",
    },
    {
      problem: "Hard to get unstuck alone",
      detail:
        "You don't have a team to rubber-duck with at 11pm before an exam. When you hit a blocker and can't work through it in 20 minutes, the task sits there for a week.",
    },
  ],

  howPassiveAgentsHelps: [
    {
      feature: "Agents work during class hours",
      benefit:
        "Assign a task Monday morning before your first lecture. Check results Monday night. The project moved forward while you were learning something else.",
    },
    {
      feature: "Keeps momentum between sessions",
      benefit:
        "You don't need to be at the keyboard every day. The project moves forward even if you only check in twice a week. No more coming back to a cold codebase.",
    },
    {
      feature: "Works with Claude Code, Codex, OpenCode, Gemini CLI",
      benefit:
        "PassiveAgents orchestrates the agents you already use — no new runtimes to learn. You bring API keys for whichever runtimes you have access to. Codex and Gemini CLI have free tiers; Claude Code requires a paid Anthropic API key. PassiveAgents itself has a free 7-day trial, then $20/month.",
    },
    {
      feature: "Every change is reviewable",
      benefit:
        "You see exactly what the agent built, line by line. You learn from what shipped, not just ship blindly. This matters when you need to explain your project in an interview.",
    },
  ],

  exampleWorkflows: [
    {
      title: "Weekend to weekday handoff",
      steps: [
        "Create tasks Sunday night based on what you want built this week.",
        "Agents pick up the work Monday through Friday while you're in class.",
        "Check in each evening to review progress and answer any blockers.",
        "Friday night, review what shipped and merge what's ready.",
        "Weekend sprint focuses on decisions and direction, not implementation.",
      ],
    },
    {
      title: "Bug fix queue",
      steps: [
        "Drop bugs into the board during class when you notice them.",
        "Agents work through the list while you're in lecture or at work.",
        "Come back to a queue of fixes ready for review, not a list of things to debug.",
        "Merge the ones that look right and leave feedback on the rest.",
      ],
    },
    {
      title: "Feature sprint for a demo",
      steps: [
        "Identify the features you need for a demo day or hackathon.",
        "Create tasks for each one and assign them to agents overnight.",
        "Agents build the scaffolding and core logic.",
        "You polish, test, and prepare the presentation.",
        "Show up with working software, not slides about working software.",
      ],
    },
  ],

  whatHumansReview: [
    "Architecture decisions that affect how the project scales",
    "Anything touching auth or user data",
    "What to demo and how to present it to recruiters or judges",
    "Code you need to understand for interviews — don't ship what you can't explain",
    "Final polish before showing the project to anyone",
  ],

  whyDifferent: [
    {
      point: "It is not a code generator you prompt once",
      detail:
        "You do not paste a prompt, get code, and start over. PassiveAgents keeps agents working on tasks until they finish. You assign work, agents persist, and you review what shipped.",
    },
    {
      point: "Your code stays on your laptop",
      detail:
        "Agents run on your machine and PassiveAgents handles coordination — your source files and API keys stay local. Note: the agent runtimes you connect (Claude Code, Codex, etc.) may send prompts or context to their own providers during normal operation.",
    },
    {
      point: "You review everything, so you learn",
      detail:
        "Every change comes back to you as a diff to review. You see how the agent approached the problem. That's useful when a recruiter asks you to walk through your project.",
    },
    {
      point: "Works on your actual side project, not a toy example",
      detail:
        "PassiveAgents connects to your real repo and runs tasks against your actual codebase. It handles the kind of incremental feature and bug work that a real project needs.",
    },
  ],

  setupOptions: [
    {
      title: "Free Trial",
      description:
        "Full access for 7 days. 6 agents, 20 active tasks, all runtimes. No credit card required.",
      cta: "Start free trial",
      href: "/pricing",
    },
    {
      title: "Pro Plan",
      description:
        "$20/month for 20 agents and 200 active tasks. Cancel anytime.",
      cta: "Subscribe",
      href: "/pricing?checkout=monthly",
    },
  ],

  faq: [
    {
      question: "Do I need to pay for API access separately?",
      answer:
        "Yes, you bring your own API keys for whichever agent runtimes you use. Many providers offer free or student tiers — Claude, OpenAI, and Google all have options that cost little or nothing to get started.",
    },
    {
      question: "Will this work with my existing GitHub repo?",
      answer:
        "Yes. The local agent manager runs alongside your existing Git repos. Agents check out branches inside your repo and work against your actual codebase.",
    },
    {
      question: "Is this cheating if I'm using it for a school project?",
      answer:
        "PassiveAgents is for personal side projects, not academic assignments. The same judgment that applies to using any AI tool for coursework applies here. Use it for your own projects, not for work you're submitting for a grade.",
    },
    {
      question: "What if an agent breaks something?",
      answer:
        "Agents work in isolated task branches inside your repo. Your main branch is untouched until you review and merge. If a change looks wrong, you simply don't merge it.",
    },
    {
      question: "How much does it cost?",
      answer:
        "The free trial gives full access for 7 days with no credit card required. The Pro plan is $20/month for 20 agents and 200 active tasks.",
    },
  ],

  cta: {
    headline: "Your side project shouldn't wait for spring break",
    description:
      "Assign tasks before class. Review what shipped at the end of the day. Your project moves forward whether or not you have time to code.",
    buttonText: "Start free trial",
    buttonHref: "/pricing",
  },
};
